import Vue from 'vue';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import tooltip from '@/mixins/components/tooltip';
import Cropper from 'cropperjs';
import path from 'path';
import _ from 'lodash';

export default Vue.component('map-marker', {
  template: require('./template.html'),
  computed: {
    markerCount() {
      const markerCount = this.droplet.map.markers.length;
      let disableClass = false;
      if (markerCount < 2) {
        disableClass = true;
      }
      return disableClass;
    },
  },
  mixins: [guid, modals, uploader],
  components: {
    uploadProgress,
    tooltip,
  },
  name: 'map-marker',
  props: ['droplet', 'index', 'marker', 'bounce'],
  data() {
    return {
      showConfirm: false,
      loading: false,
      imageFiles: [],
      showFiles: false,
      hoverDel: false,
      activeMarker: null,
      activeIndex: null,
      s3Url: process.env.S3_ASSETS_URL,
      prefix: 'drz_map_',
      bucketPath: '_droplet-assets/map/',
      thumbnails: '_droplet-assets/_thumbnails/map/',
      address: this.marker.address,
      title: this.marker.title || '',
      uploadCropError: false,
      cropperModal: false,
      cropper: null,
      openCropper: false,
      localFile: null,
      targetFile: null,
      targetName: '',
      ignoreCropper: false,
    };
  },
  watch: {
    openCropper(open) {
      if (open) {
        setTimeout(() => this.setCropper(), 300);
      }
    },
  },
  methods: {
    setIgnore() {
      this.ignoreCropper = !this.ignoreCropper;
      if (this.cropper) {
        this.cropper.destroy();
      }
      this.cropper = null;
      this.setCropper();
    },
    setCropper() {
      if (!this.ignoreCropper) {
        const image = this.$refs.imageCropper;
        this.cropper = new Cropper(image, {
          aspectRatio: 1 / 1,
          dragMode: 'move',
          autoCropArea: 1,
          zoomable: false,
        });
      }
    },
    async setAddress() {
      const payload = {};
      payload.drp = this.droplet;
      payload.index = this.index;
      payload.address = this.address.trim() === '' ? false : this.address;
      const geocoder = new google.maps.Geocoder(); // eslint-disable-line

      return new Promise((resolve, reject) => {
        geocoder.geocode(
          {
            address: this.address,
          },
          (results, status) => {
            // fetch lat and long
            if (status === google.maps.GeocoderStatus.OK) {
              // eslint-disable-line
              payload.lat = results[0].geometry.location.lat();
              payload.lng = results[0].geometry.location.lng();
              this.$store.dispatch('setMapMarkerAddress', payload);
              resolve(results);
            } else {
              const msg = `geocoder could not find address: ${this.address}, status: ${status}`;
              this.$log.debug(msg);
              reject(msg);
            }
          },
        );
      });
    },
    async setTitle() {
      const payload = {};
      payload.drp = this.droplet;
      payload.index = this.index;
      payload.title = this.title.trim() === '' ? false : this.title;
      return this.$store.dispatch('setMapMarkerTitle', payload);
    },
    async setImg(f, def) {
      let file;
      if (def) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      const payload = {};
      payload.drp = this.droplet;
      payload.index = this.index;
      payload.markerImg = file;
      return this.promise(this.$store.dispatch('setMapMarkerImg', payload), 'setting marker image');
    },
    removeMarker(i) {
      if (this.droplet.map.markers.length > 1) {
        const payload = {};
        payload.drp = this.droplet;
        payload.index = i;
        this.$store.dispatch('removeMapMarker', payload);
        this.$emit('bounce');
        let newIndex = i - 1;
        if (newIndex < 0) {
          newIndex = 0;
        }
        this.$vnode.context.changeSelectedMarker(newIndex);
      }
    },
    async fetchImageFiles() {
      try {
        this.loading = true;
        const assetsFolder = '/_droplet-assets';
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        this.imageFiles = files.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchImageFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    destroyCropper() {
      this.openCropper = false;
      this.localFile = null;
      this.uploadCropError = false;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    async uploadCropped() {
      this.uploadCropError = false;
      try {
        let file = this.targetFile;
        let name = this.targetName;
        if (!this.ignoreCropper) {
          const canvas = this.cropper.getCroppedCanvas({
            width: 48,
            height: 48,
            imageSmoothingQuality: 'high',
          });
          file = await this.getBlob(canvas);
          name = this.localFile;
        }
        const filePath = `/${this.bucketPath}${name}`;
        // send off to s3
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: file,
          thumbnail: {
            width: 35,
            srcFile: file,
            name,
            bucketPath: `${this._sitePath}/${this.thumbnails}`,
          },
        });
        // update image array
        await this.setImg(filePath);
        this.destroyCropper();
        this.targetFile = null;
        this.targetName = '';
        this.toggleCropperModal(false);
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'googleMap uploadCropped',
          });
          if (err.message !== 'Missing credentials in config') {
            this.uploadCropError = err.message || err;
          }
        }
      } finally {
        setTimeout(() => {
          if (this.showFiles) {
            this.fetchImageFiles();
          }
        }, 500);
      }
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              this.targetFile = newFile;
              let targetName = path.basename(newFile.name);
              targetName = `${this.prefix}${_.trim(targetName)}`;
              this.targetName = _.replace(targetName, /\s+/g, '_');
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // clean up the file name for s3
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `${this.prefix}${_.trim(fileName)}`;
              this.localFile = _.replace(fileName, /\s+/g, '_');
              this.toggleCropperModal(true);
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'googleMap openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    toggleCropperModal(val) {
      this.cropperModal = val;
      const content = this.$refs.cropperModal;
      if (this.cropperModal) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
        this.destroyCropper();
      }
    },
    toggleConfirm() {
      this.showConfirm = !this.showConfirm;
      const content = this.$refs.confirmModal;
      if (this.showConfirm) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    confirmDelete(file) {
      this.activeMarker = file;
      this.toggleConfirm();
    },
    async deleteMarkerImg() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this._getFromThumb(this.activeMarker));
      const thumb = path.basename(this.activeMarker);
      try {
        this.loading = true;
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted file: ${this.activeMarker.split('/').pop()}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteMarkerImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.fetchImageFiles();
      }
      // if deleting marker that is being used, reset it to default
      if (this.marker.markerImg === `/${this.bucketPath}${file}`) {
        this.setImg(`${process.env.S3_ASSETS_URL}mock-images/icons/maps-default-pin.svg`, true);
      }
    },
    matchImg(file) {
      const f = this._getFromThumb(path.basename(file));
      const media = path.basename(this.marker.markerImg);
      return f === media;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    promise(action, error) {
      const promise = new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in map: ${error}`));
        }
      });
      promise.then(() => {
        setTimeout(() => {
          this.$parent.$parent.$vnode.context.bindPlugin();
        }, 500);
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/google-map/components/marker/index.js